// @ts-nocheck
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from './ui/button';
import { FlameIcon } from './ui/icons/flame-icon';
import { useWeb3Auth } from '@/providers/Web3AuthProvider';
import { useToast } from '@/components/ui/use-toast';
import { LogoutIcon } from '@/components/ui/icons/logout-icon';
import { ConfirmDialog } from '@/components/ui/confirm-dialog';
import { VersionPill } from '@/components/ui/version-pill';
import { FloatingVoiceButton } from '@/components/ui/floating-voice-button';
import { useMutation } from 'convex/react';
import { api } from '@/convex/_generated/api';
import type { Id } from '@/convex/_generated/dataModel';
import { Img } from 'remotion';
import logoSrc from '/logo.png';

export function Home() {
	const [isFlameIconHovered, setIsFlameIconHovered] = useState(false);
	const [isLogoutHovered, setIsLogoutHovered] = useState(false);
	const [showSignOutDialog, setShowSignOutDialog] = useState(false);

	const {
		user: currentUser,
		logout,
		isWeb3AuthLoading,
		isLoggedIn,
		authAttemptCompleted,
		memberBootstrapEnabled,
		startMemberLogin,
		memberSigner,
	} = useWeb3Auth();

	const activeAccount = memberSigner;

	const navigate = useNavigate();
	const { toast } = useToast();

	const updateLastLogin = useMutation(api.users.updateLastLogin);

	useEffect(() => {
		if (memberBootstrapEnabled) {
			if (isWeb3AuthLoading || !authAttemptCompleted) return;
			if (isLoggedIn && currentUser?._id) {
				if (
					sessionStorage.getItem('loginProcessedForThisSession') !==
					'true'
				) {
					sessionStorage.setItem(
						'loginProcessedForThisSession',
						'true'
					);
					sessionStorage.setItem('hasNavigatedToAdmin', 'true');
					void updateLastLogin({
						userId: currentUser._id as Id<'users'>,
					});
				}
				if (window.location.pathname !== '/admin') navigate('/admin');
			}
			return;
		}
		if (!activeAccount) {
			if (isLoggedIn && authAttemptCompleted) {
				console.warn(
					'[Home.tsx] No activeAccount, but isLoggedIn is true and auth attempt completed. This might be after a disconnect.'
				);
				sessionStorage.removeItem('loginProcessedForThisSession');
			} else if (
				authAttemptCompleted &&
				window.location.pathname.startsWith('/admin')
			) {
				console.log(
					'[Home.tsx] No activeAccount, auth completed, on admin page. Consider redirecting to /'
				);
				sessionStorage.removeItem('loginProcessedForThisSession');
			}
			return;
		}

		if (isWeb3AuthLoading) {
			console.log(
				'[Home.tsx] Wallet connected. Web3Auth is loading/processing. Waiting...'
			);
			return;
		}

		if (authAttemptCompleted) {
			if (currentUser && currentUser._id) {
				const loginProcessed = sessionStorage.getItem(
					'loginProcessedForThisSession'
				);
				if (loginProcessed !== 'true') {
					console.log(
						'[Home.tsx] Auth completed (fresh login). User VERIFIED in Convex. Updating last login and navigating to /admin.',
						currentUser
					);
					updateLastLogin({ userId: currentUser._id as Id<'users'> })
						.then(() =>
							console.log(
								'[Home.tsx] Updated last login for user:',
								currentUser._id
							)
						)
						.catch((err) =>
							console.error(
								'[Home.tsx] Failed to update last login:',
								err
							)
						);
					sessionStorage.setItem(
						'loginProcessedForThisSession',
						'true'
					);
					// Set flag to prevent the redirect timer from firing after navigation
					sessionStorage.setItem('hasNavigatedToAdmin', 'true');

					if (window.location.pathname !== '/admin') {
						navigate('/admin');
					}
				} else {
					console.log(
						'[Home.tsx] Auth completed (session already processed). User VERIFIED in Convex. Allowing Home page view.',
						currentUser
					);
				}
			} else if (activeAccount && authAttemptCompleted) {
				// There's an active wallet but no user found yet
				// This usually happens during wallet setup before database is synced
				console.log(
					'[Home.tsx] Auth completed. Wallet connected but user data is still syncing. Waiting 1 second before redirecting...'
				);

				// Get current path before timer runs
				const currentPath = window.location.pathname;

				// Create a flag to track if navigation has happened
				const hasNavigated =
					sessionStorage.getItem('hasNavigatedToAdmin') === 'true';

				// Skip the timer if we've already navigated to admin
				if (currentPath === '/admin' || hasNavigated) {
					console.log(
						'[Home.tsx] Already on admin page or navigation has occurred. Skipping redirect timer.'
					);
					return;
				}

				// Short delay to allow database to sync
				const timerId = setTimeout(() => {
					// Only redirect if still no user after delay AND we haven't navigated to admin in the meantime
					if (
						!currentUser &&
						window.location.pathname !== '/access-request' &&
						window.location.pathname !== '/admin' &&
						sessionStorage.getItem('hasNavigatedToAdmin') !== 'true'
					) {
						console.log(
							'[Home.tsx] Still no user data after delay. Navigating to /access-request.'
						);
						sessionStorage.removeItem(
							'loginProcessedForThisSession'
						);
						navigate('/access-request');
					}
				}, 1000);

				// Clean up timer if component unmounts
				return () => {
					clearTimeout(timerId);
				};
			} else {
				console.log(
					'[Home.tsx] Auth completed. NO Convex user for current wallet. Navigating to /access-request.'
				);
				sessionStorage.removeItem('loginProcessedForThisSession'); // Clear flag if user is not found
				if (window.location.pathname !== '/access-request') {
					navigate('/access-request');
				}
			}
		} else {
			console.log(
				'[Home.tsx] Wallet connected, not loading, but auth attempt NOT YET complete. Waiting. (Web3AuthProvider might be idle or awaiting email)'
			);
		}
	}, [
		currentUser,
		activeAccount,
		isWeb3AuthLoading,
		authAttemptCompleted,
		isLoggedIn,
		logout,
		navigate,
		updateLastLogin,
		memberBootstrapEnabled,
	]);

	const handleSignOut = async () => {
		setShowSignOutDialog(false);
		try {
			// Clear all session flags first
			sessionStorage.removeItem('loginProcessedForThisSession');
			sessionStorage.removeItem('hasNavigatedToAdmin');

			await logout();

			toast({
				title: 'Signed Out',
				description: 'You have been successfully signed out.',
				duration: 3000,
			});

			// Force navigation to home page
			window.location.href = '/';
		} catch (error) {
			console.error('SIGN OUT ERROR:', error);
			toast({
				title: 'Error',
				description: 'Failed to sign out. Please try again.',
				variant: 'destructive',
				duration: 3000,
			});
			// Even on error, try to clear navigation state
			sessionStorage.removeItem('loginProcessedForThisSession');
			sessionStorage.removeItem('hasNavigatedToAdmin');

			// Force navigation to home page even on error
			window.location.href = '/';
		}
	};

	const handleDashboardNavigation = () => {
		if (currentUser) {
			navigate('/admin'); // All users go to /admin
		} else {
			console.warn(
				"[Home.tsx] Dashboard button clicked but no current user. This shouldn't happen based on button visibility."
			);
		}
	};

	const handleMemberBootstrap = async () => {
		try {
			await startMemberLogin();
		} catch {
			toast({
				title: 'Sign In Unavailable',
				description: 'Please try again in a moment.',
				variant: 'destructive',
			});
		}
	};

	return (
		<div className='relative min-h-screen'>
			<div className='orb-container'>
				<div className='orb orb-1'></div>
				<div className='orb orb-2'></div>
				<div className='orb orb-3'></div>
				<div className='orb orb-4'></div>
				<div className='orb orb-5'></div>
			</div>
			<div className='absolute inset-0 bg-black/40 z-0' />
			<main className='relative z-10'>
				<div className='container mx-auto px-4 py-20'>
					<div className='flex flex-col items-center justify-center space-y-12'>
						<div className='w-full max-w-3xl mx-auto text-center glass-morphism p-8 rounded-lg'>
							<div className='flex flex-col items-center space-y-2'>
								<Img
									src={logoSrc}
									alt='Prometheus'
									className='w-[500px] h-auto'
								/>
								<p className='text-3xl text-orange-500'>
									Stealing Fire From The Gods
								</p>
							</div>

							<div className='pt-8 flex flex-col gap-4 items-center'>
								{isWeb3AuthLoading && (
									<Button
										size='lg'
										className='bg-gradient-to-b from-pink-500 to-purple-600 text-white px-12 py-6 h-auto w-64'
										disabled
									>
										<span className='text-2xl font-semibold'>
											LOADING...
										</span>
									</Button>
								)}

								{!isWeb3AuthLoading &&
									memberBootstrapEnabled &&
									!isLoggedIn && (
										<Button
											size='lg'
											className='bg-gradient-to-b from-pink-500 to-purple-600 text-white px-12 py-6 h-auto w-64'
											onClick={handleMemberBootstrap}
										>
											<span className='text-2xl font-semibold'>
												SIGN IN
											</span>
										</Button>
									)}

								{!isWeb3AuthLoading && currentUser && (
									// User is fully logged in and recognized by Convex
									<Button
										size='lg'
										onMouseEnter={() =>
											setIsFlameIconHovered(true)
										}
										onMouseLeave={() =>
											setIsFlameIconHovered(false)
										}
										className='bg-gradient-to-b from-pink-500 to-purple-600 hover:from-pink-600 hover:to-purple-700 text-white px-12 py-6 h-auto w-64'
										onClick={handleDashboardNavigation}
									>
										<div className='flex items-center justify-center gap-1.5'>
											<FlameIcon
												isHovered={isFlameIconHovered}
											/>
											<span className='text-2xl font-semibold'>
												DASHBOARD
											</span>
										</div>
									</Button>
								)}

								{(activeAccount || currentUser) &&
									!isWeb3AuthLoading && (
										<Button
											variant='outline'
											size='default'
											onMouseEnter={() =>
												setIsLogoutHovered(true)
											}
											onMouseLeave={() =>
												setIsLogoutHovered(false)
											}
											onClick={() =>
												setShowSignOutDialog(true)
											}
											className='bg-black/20 border border-pink-500/30 hover:bg-pink-500/10 hover:border-pink-500/50 text-pink-500 hover:text-pink-400 px-4 py-2 h-auto transition-all duration-200'
										>
											<div className='flex items-center justify-center gap-1.5'>
												<LogoutIcon
													isHovered={isLogoutHovered}
												/>
												<span className='font-medium'>
													Sign Out
												</span>
											</div>
										</Button>
									)}
							</div>
						</div>
					</div>
				</div>
			</main>
			<ConfirmDialog
				isOpen={showSignOutDialog}
				onClose={() => setShowSignOutDialog(false)}
				onConfirm={handleSignOut}
				title='Sign Out'
				description='Are you sure you want to sign out?'
				confirmText='Yes, Sign Out'
				cancelText='Cancel'
			/>
			<FloatingVoiceButton />
			<VersionPill />
		</div>
	);
}
